// =====================================================================
// My Agency V2 — symbol library (ARTBOARD-SPEC.md §3, SYM-01…SYM-32).
//
// ZERO-TOLERANCE rule (Brief Rule 2): every path/viewBox/fill/stroke is
// VERBATIM from the handoff. Attributes that vary per placement
// (width/height/stroke color/stroke-width) are props with the spec's
// base variant as default; the per-board sections of the spec state
// which variant to pass. Do NOT edit path data.
//
// Usage: `<PlusIcon />` for the base variant, or e.g.
// `<ChevronRightIcon size={11} stroke="#1668D9" />` for a documented
// per-board variant. All icons also export under their spec ID via the
// `SYM` map for parity audits.
//
// FLAGS (Brief Rule 3): country flags are NOT part of this module and
// are NEVER emoji and NEVER the handoff's CSS-gradient chips. Use the
// platform's IMAGE-flag rendering via `FlagChip.js` in this folder
// (same idiom as the inline `Flag` in components/agency/PlayersGrid.js:
// <img src={country.image_url} …>). The spec's "CSS flag chips" table
// (§2) documents SIZES only — 20×14 r3 (player cards) and 18×13 r2.5
// (phone input / country list) — which FlagChip implements.
// =====================================================================
import React from "react";

// ── SYM-01 Plus ──────────────────────────────────────────────────────
export const PlusIcon = ({ size = 15, stroke = "#fff", strokeWidth = 2.4, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" {...rest}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

// ── SYM-02 Overflow dots (vertical kebab) — FILLED, no stroke ────────
export const OverflowDotsIcon = ({ size = 16, fill = "#64748B", ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} {...rest}>
    <circle cx="12" cy="5" r="1.7" />
    <circle cx="12" cy="12" r="1.7" />
    <circle cx="12" cy="19" r="1.7" />
  </svg>
);

// ── SYM-03 Chevron right ─────────────────────────────────────────────
export const ChevronRightIcon = ({ size = 14, stroke = "#94A3B8", strokeWidth = 2.2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="m9 18 6-6-6-6" />
  </svg>
);

// ── SYM-04 Chevron down ──────────────────────────────────────────────
export const ChevronDownIcon = ({ size = 13, stroke = "#94A3B8", strokeWidth = 2.2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

// ── SYM-05 Chevron up ────────────────────────────────────────────────
export const ChevronUpIcon = ({ size = 15, stroke = "#94A3B8", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="m18 15-6-6-6 6" />
  </svg>
);

// ── SYM-06 Close X ───────────────────────────────────────────────────
export const CloseIcon = ({ size = 15, stroke = "#64748B", strokeWidth = 2.2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" {...rest}>
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

// ── SYM-07 Search (magnifier) ────────────────────────────────────────
export const SearchIcon = ({ size = 15, stroke = "#94A3B8", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" {...rest}>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

// ── SYM-08 Link / chain (Linked pill) ────────────────────────────────
export const LinkIcon = ({ size = 10, stroke = "#1668D9", strokeWidth = 2.4, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

// ── SYM-09 Globe (partner website link) ──────────────────────────────
export const GlobeIcon = ({ size = 11, stroke = "#1668D9", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a15.3 15.3 0 0 1 0 20 15.3 15.3 0 0 1 0-20" />
  </svg>
);

// ── SYM-10 Globe variant (players empty-state icon) ──────────────────
export const GlobeEmptyIcon = ({ size = 24, stroke = "#2283FF", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
    <path d="M2 12h20" />
  </svg>
);

// ── SYM-11 Clock (pending / expiring) ────────────────────────────────
export const ClockIcon = ({ size = 15, stroke = "#64748B", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" {...rest}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

// ── SYM-12 Warning triangle ──────────────────────────────────────────
export const WarningTriangleIcon = ({ size = 16, stroke = "#B45309", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
);

// ── SYM-13 Alert circle (inline form error banner) ───────────────────
export const AlertCircleIcon = ({ size = 14, stroke = "#DC2626", strokeWidth = 2, style = { flex: "none", marginTop: 1 }, ...rest }) => (
  <svg style={style} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v4" />
    <path d="M12 16h.01" />
  </svg>
);

// ── SYM-14 Checkmark ─────────────────────────────────────────────────
export const CheckIcon = ({ size = 14, stroke = "#16A34A", strokeWidth = 2.6, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// ── SYM-15 Calendar ──────────────────────────────────────────────────
export const CalendarIcon = ({ size = 14, stroke = "#94A3B8", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" {...rest}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

// ── SYM-16 Mail (envelope) — no linecap/linejoin attrs ───────────────
export const MailIcon = ({ size = 14, stroke = "#94A3B8", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} {...rest}>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m2 7 10 6 10-6" />
  </svg>
);

// ── SYM-17 Phone (handset) ───────────────────────────────────────────
// NOTE: the v3 phone input (boards 3a/3b/3c) has NO handset icon — it
// uses the flag + dial-code trigger instead.
export const PhoneIcon = ({ size = 14, stroke = "#94A3B8", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

// ── SYM-18 Copy (two rectangles) — no linecap/linejoin attrs ─────────
export const CopyIcon = ({ size = 13, stroke = "#64748B", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} {...rest}>
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

// ── SYM-19 User (person) ─────────────────────────────────────────────
export const UserIcon = ({ size = 14, stroke = "#64748B", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

// ── SYM-20 Pencil / edit ─────────────────────────────────────────────
export const PencilIcon = ({ size = 14, stroke = "#64748B", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  </svg>
);

// ── SYM-21 Shield (make admin) ───────────────────────────────────────
export const ShieldIcon = ({ size = 14, stroke = "#64748B", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1 1 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
  </svg>
);

// ── SYM-22 Shield-X (remove as admin dialog icon) ────────────────────
export const ShieldXIcon = ({ size = 18, stroke = "#DC2626", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1 1 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    <path d="m14.5 9-5 5" />
    <path d="m9.5 9 5 5" />
  </svg>
);

// ── SYM-23 Trash ─────────────────────────────────────────────────────
export const TrashIcon = ({ size = 14, stroke = "#DC2626", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="M3 6h18" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

// ── SYM-24 Bell (send license reminder) ──────────────────────────────
export const BellIcon = ({ size = 14, stroke = "#64748B", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

// ── SYM-25 Sort arrows (up-down) ─────────────────────────────────────
export const SortArrowsIcon = ({ size = 13, stroke = "#64748B", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="m3 8 4-4 4 4" />
    <path d="M7 4v16" />
    <path d="m21 16-4 4-4-4" />
    <path d="M17 20V4" />
  </svg>
);

// ── SYM-26 Resend / refresh (two arcs) ───────────────────────────────
export const ResendIcon = ({ size = 14, stroke = "#64748B", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="M21.5 2v6h-6" />
    <path d="M2.5 22v-6h6" />
    <path d="M2 11.5a10 10 0 0 1 18.8-4.3" />
    <path d="M22 12.5a10 10 0 0 1-18.8 4.2" />
  </svg>
);

// ── SYM-27 Transfer arrows ("What this changes") ─────────────────────
export const TransferArrowsIcon = ({ size = 13, stroke = "#1668D9", strokeWidth = 2.2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="M8 3 4 7l4 4" />
    <path d="M4 7h16" />
    <path d="m16 21 4-4-4-4" />
    <path d="M20 17H4" />
  </svg>
);

// ── SYM-28 Archive box ("What stays with …") ─────────────────────────
export const ArchiveBoxIcon = ({ size = 13, stroke = "#64748B", strokeWidth = 2.2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <rect x="2" y="4" width="20" height="5" rx="1" />
    <path d="M4 9v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9" />
    <path d="M10 13h4" />
  </svg>
);

// ── SYM-29 Image placeholder (logo upload tile) ──────────────────────
export const ImagePlaceholderIcon = ({ size = 16, stroke = "#94A3B8", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-3.09-3.09a2 2 0 0 0-2.83 0L6 21" />
  </svg>
);

// ── SYM-30 Users-plus (agents empty state icon) ──────────────────────
export const UsersPlusIcon = ({ size = 24, stroke = "#2283FF", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M19 8v6" />
    <path d="M22 11h-6" />
  </svg>
);

// ── SYM-31 Layers (partners empty state icon) ────────────────────────
export const LayersIcon = ({ size = 24, stroke = "#2283FF", strokeWidth = 2, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="M12 2 2 7l10 5 10-5-10-5Z" />
    <path d="m2 17 10 5 10-5" />
    <path d="m2 12 10 5 10-5" />
  </svg>
);

// ── SYM-32 Map pin (1i club/country row — superseded board only) ─────
export const MapPinIcon = ({ size = 14, stroke = "#94A3B8", strokeWidth = 2, style = { flex: "none" }, ...rest }) => (
  <svg style={style} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

// Spec-ID map for parity audits (SYM-nn → component).
export const SYM = {
  "SYM-01": PlusIcon,
  "SYM-02": OverflowDotsIcon,
  "SYM-03": ChevronRightIcon,
  "SYM-04": ChevronDownIcon,
  "SYM-05": ChevronUpIcon,
  "SYM-06": CloseIcon,
  "SYM-07": SearchIcon,
  "SYM-08": LinkIcon,
  "SYM-09": GlobeIcon,
  "SYM-10": GlobeEmptyIcon,
  "SYM-11": ClockIcon,
  "SYM-12": WarningTriangleIcon,
  "SYM-13": AlertCircleIcon,
  "SYM-14": CheckIcon,
  "SYM-15": CalendarIcon,
  "SYM-16": MailIcon,
  "SYM-17": PhoneIcon,
  "SYM-18": CopyIcon,
  "SYM-19": UserIcon,
  "SYM-20": PencilIcon,
  "SYM-21": ShieldIcon,
  "SYM-22": ShieldXIcon,
  "SYM-23": TrashIcon,
  "SYM-24": BellIcon,
  "SYM-25": SortArrowsIcon,
  "SYM-26": ResendIcon,
  "SYM-27": TransferArrowsIcon,
  "SYM-28": ArchiveBoxIcon,
  "SYM-29": ImagePlaceholderIcon,
  "SYM-30": UsersPlusIcon,
  "SYM-31": LayersIcon,
  "SYM-32": MapPinIcon,
};
